import {Image, Link} from '@shopify/hydrogen';
import {CountryCode} from '@shopify/hydrogen/storefront-api-types';
import {motion} from 'framer-motion';
import clsx from 'clsx';

import {
  getProductOptionString,
  hasMultipleProductOptions,
} from '../../utils/productOptions';

import type {ProductWithNodes, SanityProductIcons} from '../../types';

import Badge from '../elements/Badge';
import MoneyCompareAtPrice from './money/CompareAtPrice.client';
import MoneyPrice from './money/Price.client';

import {contentVars} from '../ui/animationVars';
import {StarRating} from '../elements/StarRating.client';
import ProductBadge from '../elements/ProductBadge';
import {useInventory} from '../../hooks/useInventory';
import ProductIcons from './ProductIcons';

type ProductCardProps = {
  imageAspectClassName?: string;
  storefrontProduct: ProductWithNodes & {productIcons: SanityProductIcons};
  countryCode?: CountryCode;
};

export default function ProductCard({
  imageAspectClassName = 'aspect-h-1 aspect-w-1',
  storefrontProduct,
  countryCode,
}: ProductCardProps) {
  const [showPreorder, showOutOfStock] = useInventory({
    countryCode: countryCode || 'NZ',
    gid: storefrontProduct.id || '',
    preOrderConfig: storefrontProduct.preOrderConfig,
  });

  const firstVariant = storefrontProduct.variants.nodes[0];

  const multipleProductOptions = hasMultipleProductOptions(
    storefrontProduct.options,
  );
  const productOptions = getProductOptionString(storefrontProduct.options);

  //GTM
  function handleClick() {
    //add product click to datalayer
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.dataLayer.push({
      event: 'productClick',
      ecommerce: {
        click: {
          actionField: {list: 'Product List'}, // Optional list property.
          products: [
            {
              name: storefrontProduct.title, // Name or ID is required.
              id: storefrontProduct.id,
              price: firstVariant.priceV2.amount,
            },
          ],
        },
      },
    });
  }

  if (firstVariant == null) {
    return null;
  }

  return (
    <motion.div
      variants={contentVars}
      className="group relative mx-auto flex h-min w-full max-w-[210px] flex-col justify-between gap-y-2"
    >
      <div
        className={clsx([
          imageAspectClassName,
          'bg-lightGray relative flex items-center justify-center overflow-hidden rounded object-cover transition-[border-radius] duration-500 ease-out',
          'rounded-xl',
        ])}
        data-testid={`product-card-link-${storefrontProduct?.handle}`}
      >
        <Link
          onClick={() => handleClick()}
          className="absolute left-0 top-0 h-full w-full"
          to={`/products/${storefrontProduct?.handle}`}
        >
          {firstVariant.image && (
            <Image
              width="inherit"
              height="inherit"
              className="transition-standard absolute h-full w-full transform bg-cover bg-center object-cover object-center ease-in-out hover:scale-105"
              data={firstVariant.image}
              loaderOptions={{crop: 'center'}}
              alt={storefrontProduct?.title || 'product image'}
              data-testid={`product-card-image-${storefrontProduct?.handle}`}
            />
          )}

          {/* Badges */}
          <div className="absolute left-4 top-4">
            {/* Sale */}
            {firstVariant?.availableForSale &&
              firstVariant?.compareAtPriceV2 && (
                <Badge label="Sale" tone="critical" />
              )}
            {/* Sold out */}
          </div>
        </Link>
      </div>
      <Link
        onClick={() => handleClick()}
        to={`/products/${storefrontProduct.handle}`}
        className="blockH9 text-center"
      >
        {(showPreorder || showOutOfStock) && (
          <div className="mb-2">
            <ProductBadge
              variant="orange"
              label={showPreorder ? 'Pre-order' : 'Sold out'}
            />
          </div>
        )}

        <div className="space-y-1">
          {/* Title */}
          <span>{storefrontProduct.title}</span>

          {/* Product options */}
          {multipleProductOptions && (
            <div className="blockH10 text-orange">{productOptions}</div>
          )}
        </div>
      </Link>
      {/* Price / compare at price */}
      {firstVariant?.availableForSale ? (
        <div
          className="blockH9 flex flex-col items-center justify-center font-bold md:flex-row"
          data-testid={`product-card-price-${storefrontProduct?.handle}`}
        >
          {firstVariant?.compareAtPriceV2 && (
            <span className="blockH9 line-through">
              <MoneyCompareAtPrice money={firstVariant.compareAtPriceV2} />
            </span>
          )}
          <MoneyPrice
            money={firstVariant.priceV2}
            className={clsx([firstVariant?.compareAtPriceV2 && 'text-orange'])}
          />
        </div>
      ) : (
        <div className="richH7 flex justify-center font-bold">Sold out</div>
      )}
      {/* Icons */}
      {storefrontProduct.productIcons && (
        <div className="mx-auto w-fit">
          <ProductIcons
            icons={storefrontProduct.productIcons}
            hasSmallerIcons
          />
        </div>
      )}
      {/* Review star rating */}
      <div className="pt-4">
        <StarRating
          gid={storefrontProduct?.id}
          isSmall
          stackStarsAndText={{mobile: true, md: true}}
          align="center"
        />
      </div>
    </motion.div>
  );
}
